/**
 * Source catalog display helpers — extracted from SourceCatalog.tsx for testing.
 *
 * adapterNameFor() maps an API path to the adapter name used by
 * /api/health/detailed. Getting it wrong means a degraded adapter shows
 * no warning chip in the SOURCES modal even though the health endpoint
 * reports its status correctly.
 */

import type { SourceCategory, SourceStatus } from "@chonburi/shared";

export const STATUS_COLOR: Record<SourceStatus, string> = {
  live: "var(--good)",
  ready: "var(--data)",
  planned: "var(--warn)",
  research: "var(--text-3)",
  stub: "var(--text-3)",
};

export const CATEGORY_LABEL: Record<SourceCategory, string> = {
  mobility: "MOB",
  incidents: "INC",
  environment: "ENV",
  imagery: "IMG",
  vibes: "VIB",
  infrastructure: "INF",
  maritime: "MAR",
  "open-data": "OPN",
  campus: "LEG",
};

/**
 * The catalog's `status` says how far the *integration* got — it is not a
 * freshness signal. A green "LIVE" chip on a daily reservoir report or a
 * camera-location registry reads as a promise of live data that it cannot
 * keep, so the chip says what it actually means.
 *
 * Runtime freshness is carried by the adapter health chip beside it, which
 * is fed by /api/health/detailed.
 */
export const STATUS_LABEL: Record<SourceStatus, string> = {
  live: "Active",
  ready: "Ready",
  planned: "Planned",
  research: "Research",
  stub: "Stub",
};

/** Full explanation for the status chip's tooltip and accessible name. */
export const STATUS_MEANING: Record<SourceStatus, string> = {
  live: "Adapter wired and running. This does not mean the data itself refreshes continuously.",
  ready: "Adapter scaffolded — flip a switch or drop an API key to go live.",
  planned: "Endpoint known, adapter not built yet.",
  research: "The data exists, but the public endpoint is not established.",
  stub: "Adapter returns placeholder data until a real source is wired.",
};

/**
 * Map an apiPath ending segment / pattern → adapter name used by /api/health/detailed.
 * Adapter names come from the second arg of `safeFeed(c, fn, "adapter-name")` in apps/api/src/index.ts.
 * Keep this in sync when new routes are added.
 */
export const API_PATH_TO_ADAPTER: Array<[RegExp, string]> = [
  [/\/api\/open-data\/catalog$/, "open-data"],
  [/\/api\/water\/reservoirs$/, "rid-reservoirs"],
  [/\/api\/water\/rain-gauges$/, "rain-gauges"],
  [/\/api\/water\/gauges$/, "water-gauges"],
  [/\/api\/cctv\/dwr$/, "dwr-cctv"],
  [/\/api\/incidents\/city-reports$/, "city-reports"],
  [/\/api\/incidents\/itic$/, "itic"],
  [/\/api\/news$/, "news"],
  [/\/api\/weather$/, "weather"],
  [/\/api\/precip-nowcast$/, "precip-nowcast"],
  [/\/api\/air-quality$/, "air-quality"],
  [/\/api\/air-quality\/trend$/, "air-quality-trend"],
  [/\/api\/air-quality\/aqicn$/, "aqicn"],
  [/\/api\/cctv\/longdo$/, "cctv"],
  [/\/api\/trends$/, "trends"],
  [/\/api\/datago\/datasets$/, "datago-datasets"],
  [/\/api\/datago\/reservoirs$/, "reservoirs"],
  [/\/api\/datago\/disasters$/, "disasters"],
  [/\/api\/datago\/fahfon$/, "fahfon"],
  [/\/api\/datago\/points$/, "datago-points"],
  [/\/api\/datago\/provincial-kpis$/, "datago-provincial-kpis"],
  [/\/api\/marine$/, "marine"],
  [/\/api\/tides$/, "tides"],
  [/\/api\/gistda\/poi$/, "gistda-poi"],
  [/\/api\/gistda\/solar$/, "gistda-solar"],
  [/\/api\/gistda\/landuse$/, "gistda-landuse"],
  [/\/api\/nasa\/earth-readings$/, "nasa-power"],
  [/\/api\/social\/facebook$/, "facebook"],
  [/\/api\/markets$/, "markets"],
  [/\/api\/maritime\/ais$/, "ais"],
];

/**
 * Find the adapter name that owns the given API path.
 * Returns null if the path doesn't match any known adapter.
 */
export function adapterNameFor(apiPath: string | undefined): string | null {
  if (!apiPath) return null;
  for (const [re, name] of API_PATH_TO_ADAPTER) if (re.test(apiPath)) return name;
  return null;
}
